import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faDna, 
    faAngleDown, 
    faAngleRight, 
    faExclamationTriangle,
    faInfo, 
    faInfoCircle 
} from '@fortawesome/free-solid-svg-icons';
import './WgsAnalysisCard.css';

/**
 * Specialized component for rendering WGS (Whole Genome Sequencing) analysis data
 */
const WgsAnalysisCard = ({ data, animationDelay }) => {
    // Debug logging
    console.log('WGS Analysis Card received data:', data);
    
    const [expanded, setExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState('diseases'); // 'diseases' or 'pharmacogenetics'
    
    // Early return if no data
    if (!data) {
        console.error('WGS Analysis Card received no data');
        return null;
    }
    
    // Handle missing details
    if (!data.details) {
        console.warn('WGS Analysis Card: details not found in data');
        
        // Render a minimal card with the available information
        const statusInfo = {
            color: '#9e9e9e',
            bgColor: 'rgba(158, 158, 158, 0.1)'
        };
        
        return (
            <div className="s2-wgs-card" style={{ animationDelay }}>
                <div className="s2-wgs-header">
                    <FontAwesomeIcon icon={faDna} className="s2-wgs-icon" />
                    <div className="s2-wgs-title">WGS Analysis</div>
                </div>
                <div className="s2-wgs-content">
                    {data.value && (
                        <div className="s2-wgs-value">{data.value}</div>
                    )}
                    {data.interpretation && (
                        <div className="s2-wgs-interpretation" style={{ color: statusInfo.color }}>
                            {data.interpretation}
                        </div>
                    )}
                    {data.notes && (
                        <div className="s2-wgs-notes">Notes: {data.notes}</div>
                    )}
                    {data.source && (
                        <div className="s2-wgs-source">{data.source}</div>
                    )}
                </div>
            </div>
        );
    }
    
    const { details } = data;
    console.log('WGS Analysis Card details:', details);
    
    const hasDiseasesData = details.diseases && details.diseases.length > 0;
    const hasPharmacogeneticsData = details.pharmacogenetics && details.pharmacogenetics.length > 0;
    const hasTraitsData = details.traits && details.traits.length > 0;
    
    console.log('WGS data availability:', { hasDiseasesData, hasPharmacogeneticsData, hasTraitsData });
    
    // Count data for summary
    const diseasesCount = hasDiseasesData ? details.diseases.length : 0;
    const pharmacogeneticsCount = hasPharmacogeneticsData ? details.pharmacogenetics.length : 0;
    const traitsCount = hasTraitsData ? details.traits.length : 0;
    
    // Group pharmacogenetics by drug name for more compact display
    const drugGroups = hasPharmacogeneticsData ? 
        details.pharmacogenetics.reduce((groups, item) => {
            const drugName = item.drug_name;
            if (!groups[drugName]) {
                groups[drugName] = [];
            }
            groups[drugName].push(item);
            return groups;
        }, {}) : {};
    
    // Group traits by category
    const traitCategories = hasTraitsData ?
        details.traits.reduce((categories, trait) => {
            const category = trait.category || 'Other';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(trait);
            return categories;
        }, {}) : {};

    return (
        <div className="s2-wgs-card">
            <div className="s2-wgs-header" onClick={() => setExpanded(!expanded)}>
                <div className="s2-wgs-icon">
                    <FontAwesomeIcon 
                        icon={faDna} 
                        className="s2-wgs-icon-dna"
                    />
                </div>
                <div className="s2-wgs-title-container">
                    <div className="s2-wgs-title">Genomic Analysis</div>
                    <div className="s2-wgs-summary">
                        {diseasesCount > 0 && (
                            <span className="s2-wgs-pill s2-wgs-pill-diseases">
                                {diseasesCount} Diseases
                            </span>
                        )}
                        {pharmacogeneticsCount > 0 && (
                            <span className="s2-wgs-pill s2-wgs-pill-pharma">
                                {pharmacogeneticsCount} Drug interactions
                            </span>
                        )}
                        {traitsCount > 0 && (
                            <span className="s2-wgs-pill s2-wgs-pill-traits">
                                {traitsCount} Traits
                            </span>
                        )}
                    </div>
                </div>
                <div className="s2-wgs-expand-icon">
                    <FontAwesomeIcon 
                        icon={expanded ? faAngleDown : faAngleRight} 
                    />
                </div>
            </div>
            
            {expanded && (
                <div className="s2-wgs-content s2-anim-fadeIn">
                    <div className="s2-wgs-tabs">
                        {hasDiseasesData && (
                            <div 
                                className={`s2-wgs-tab ${activeTab === 'diseases' ? 's2-wgs-tab-active' : ''}`}
                                onClick={() => setActiveTab('diseases')}
                            >
                                Diseases
                            </div>
                        )}
                        {hasPharmacogeneticsData && (
                            <div 
                                className={`s2-wgs-tab ${activeTab === 'pharmacogenetics' ? 's2-wgs-tab-active' : ''}`}
                                onClick={() => setActiveTab('pharmacogenetics')}
                            >
                                Pharmacogenetics
                            </div>
                        )}
                        {hasTraitsData && (
                            <div 
                                className={`s2-wgs-tab ${activeTab === 'traits' ? 's2-wgs-tab-active' : ''}`}
                                onClick={() => setActiveTab('traits')}
                            >
                                Traits
                            </div>
                        )}
                    </div>
                    
                    {/* Diseases Tab Content */}
                    {activeTab === 'diseases' && hasDiseasesData && (
                        <div className="s2-wgs-tab-content">
                            <table className="s2-wgs-table">
                                <thead>
                                    <tr>
                                        <th>Disease</th>
                                        <th>Classification</th>
                                        <th>Severity</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.diseases.map((disease, index) => (
                                        <tr key={index} className="s2-wgs-table-row">
                                            <td>{disease.disease_name}</td>
                                            <td>{disease.acmg_classification?.replace(/_/g, ' ')}</td>
                                            <td className={`s2-wgs-severity-${disease.severity?.toLowerCase() || 'normal'}`}>
                                                {disease.severity}
                                            </td>
                                            <td className="s2-wgs-details-cell">
                                                <div className="s2-wgs-details-popup">
                                                    <FontAwesomeIcon icon={faInfo} className="s2-wgs-details-icon" />
                                                    <div className="s2-wgs-details-content">
                                                        <div><strong>RSID:</strong> {disease.rsid || 'N/A'}</div>
                                                        <div><strong>Position:</strong> {disease.position || 'N/A'}</div>
                                                        <div><strong>Impact:</strong> {disease.impact || 'N/A'}</div>
                                                        <div><strong>HGVSC:</strong> {disease.hgvsc || 'N/A'}</div>
                                                        {disease.clinvar_significance && (
                                                            <div><strong>ClinVar:</strong> {disease.clinvar_significance}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {/* Pharmacogenetics Tab Content */}
                    {activeTab === 'pharmacogenetics' && hasPharmacogeneticsData && (
                        <div className="s2-wgs-tab-content">
                            {Object.entries(drugGroups).map(([drugName, items], drugIndex) => (
                                <div key={drugIndex} className="s2-wgs-drug-group">
                                    <div className="s2-wgs-drug-name">
                                        {drugName.split(';').join(', ')}
                                    </div>
                                    <table className="s2-wgs-table">
                                        <thead>
                                            <tr>
                                                <th>Gene</th>
                                                <th>Genotype</th>
                                                <th>Category</th>
                                                <th>Evidence</th>
                                                <th>Severity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item, index) => (
                                                <tr key={index} className="s2-wgs-table-row">
                                                    <td>{item.gene}</td>
                                                    <td>{item.genotype}</td>
                                                    <td>{item.phenotype_category}</td>
                                                    <td>{item.level_of_evidence}</td>
                                                    <td className={`s2-wgs-severity-${item.severity?.toLowerCase() || 'normal'}`}>
                                                        {item.severity}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Traits Tab Content */}
                    {activeTab === 'traits' && hasTraitsData && (
                        <div className="s2-wgs-tab-content">
                            {Object.entries(traitCategories).map(([category, traits], catIndex) => (
                                <div key={catIndex} className="s2-wgs-trait-category">
                                    <div className="s2-wgs-category-name">{category}</div>
                                    <div className="s2-wgs-traits-grid">
                                        {traits.map((trait, index) => (
                                            <div key={index} className="s2-wgs-trait-card">
                                                <div className="s2-wgs-trait-header">
                                                    <div className="s2-wgs-trait-name">{trait.trait_name}</div>
                                                    <div className={`s2-wgs-trait-level s2-wgs-level-${trait.level || 0}`}>
                                                        {trait.level_text}
                                                    </div>
                                                </div>
                                                <div className="s2-wgs-trait-details">
                                                    <div className="s2-wgs-trait-gene">
                                                        <span className="s2-wgs-trait-label">Gene:</span> {trait.gene}
                                                    </div>
                                                    <div className="s2-wgs-trait-genotype">
                                                        <span className="s2-wgs-trait-label">Genotype:</span> {trait.patient_genotype}
                                                    </div>
                                                    <div className="s2-wgs-trait-description">
                                                        {trait.genotype_specific_text}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            
            {data.source && (
                <div className="s2-wgs-footer">
                    <span className="s2-wgs-source">
                        <FontAwesomeIcon icon={faInfoCircle} className="s2-wgs-source-icon" />
                        {data.source}
                    </span>
                </div>
            )}
        </div>
    );
};

export default WgsAnalysisCard;
